import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineGrid,
  Button,
  Badge,
  Banner,
  ProgressBar,
  Divider,
  Spinner,
} from '@shopify/polaris';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '../lib/api';

interface Plan {
  id: string;
  name: string;
  slug: string;
  price: number;
  billing_interval: string;
  included_tryons: number;
  overage_price: number;
  features: string[];
  is_popular: boolean;
}

// Mirrors what GET /billing/subscription actually returns
// (SubscriptionService.getUsageStats). The interface used to declare
// tryons_used/tryons_limit, which the server never sends, so every read was
// undefined and the card rendered "0 / 0" no matter how many try-ons the shop
// had spent.
interface Usage {
  used: number;
  limit: number;
  remaining: number;
  periodEnd: string;
}

// Fallback plans if the /billing/plans API can't be reached. Prices and
// allowances MUST match the seeded shopify_plans rows (supabase migration
// 20260622_001_seed_shopify_plans.sql) and the Shopify Managed Pricing plans.
const FALLBACK_PLANS: Plan[] = [
  { id: 'free',    name: 'Free',    slug: 'free',    price: 0,   billing_interval: 'monthly', included_tryons: 10,   overage_price: 0, features: ['10 try-ons/mo', 'Customizable try-on widget', 'Email support'], is_popular: false },
  { id: 'starter', name: 'Starter', slug: 'starter', price: 29,  billing_interval: 'monthly', included_tryons: 100,  overage_price: 0, features: ['100 try-ons/mo', 'Customizable try-on widget', 'Priority support'], is_popular: false },
  { id: 'growth',  name: 'Growth',  slug: 'growth',  price: 99,  billing_interval: 'monthly', included_tryons: 350,  overage_price: 0, features: ['350 try-ons/mo', 'Customizable try-on widget', 'Priority support'], is_popular: true },
  { id: 'scale',   name: 'Scale',   slug: 'scale',   price: 299, billing_interval: 'monthly', included_tryons: 1050, overage_price: 0, features: ['1,050 try-ons/mo', 'Customizable try-on widget', 'Priority support'], is_popular: false },
];

function redirectToManagedPricing(url: string) {
  // Must use window.top to break out of the Shopify Admin iframe
  if (window.top) {
    window.top.location.href = url;
  } else {
    window.location.href = url;
  }
}

export default function Billing() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currentPlan, setCurrentPlan] = useState<Plan | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const [plansData, subData] = await Promise.all([
          apiGet<any>('/billing/plans').catch(() => null),
          apiGet<any>('/billing/subscription').catch(() => null),
        ]);

        const loadedPlans: Plan[] = plansData?.success ? plansData.data : FALLBACK_PLANS;
        setPlans(loadedPlans);

        if (subData?.success && subData.data.plan) {
          setUsage(subData.data.usage);
          const active = loadedPlans.find(p => p.id === subData.data.plan.id);
          setCurrentPlan(active || null);
        } else {
          setCurrentPlan(loadedPlans.find(p => p.slug === 'free') || null);
        }
      } catch {
        setPlans(FALLBACK_PLANS);
        setCurrentPlan(FALLBACK_PLANS[0]);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const handleSubscribe = useCallback(async (plan: Plan) => {
    if (plan.slug === currentPlan?.slug) return;
    setSubscribing(plan.slug);
    setError(null);

    try {
      const data = await apiPost<any>('/billing/subscribe', { planSlug: plan.slug }).catch((err: Error) => {
        setError(err.message || 'Failed to subscribe');
        return null;
      });
      if (!data) return;

      if (!data.success) {
        setError(data.error?.message || 'Failed to subscribe');
        return;
      }

      if (data.data.managedPricingUrl) {
        // Shopify Managed Pricing — redirect to Shopify's native plan selection page
        redirectToManagedPricing(data.data.managedPricingUrl);
      } else if (data.data.subscribed) {
        // Free plan activated immediately
        setCurrentPlan(plan);
        setUsage(prev => (prev ? { ...prev, used: 0, limit: plan.included_tryons, remaining: plan.included_tryons } : prev));
        setSuccessMessage(`You're now on the ${plan.name} plan!`);
      }
    } catch {
      setError('Network error — please try again');
    } finally {
      setSubscribing(null);
    }
  }, [currentPlan]);

  const handleManageSubscription = useCallback(async () => {
    setSubscribing('manage');
    setError(null);
    try {
      const data = await apiPost<any>('/billing/cancel', {});
      if (!data.success) {
        setError(data.error?.message || 'Failed to open subscription management');
        return;
      }
      if (data.data.managedPricingUrl) {
        redirectToManagedPricing(data.data.managedPricingUrl);
      }
    } catch {
      setError('Network error — please try again');
    } finally {
      setSubscribing(null);
    }
  }, []);

  const usagePercent = usage && (usage.limit ?? 0) > 0
    ? Math.min(100, Math.round(((usage.used ?? 0) / (usage.limit ?? 1)) * 100))
    : 0;

  return (
    <Page
      title="Subscription"
      backAction={{ content: 'Dashboard', onAction: () => navigate('/') }}
    >
      <Layout>

        {/* ── Success / error banners ── */}
        {successMessage && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSuccessMessage(null)}>
              <p>{successMessage}</p>
            </Banner>
          </Layout.Section>
        )}
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* ── Current plan + usage ── */}
        {!loading && currentPlan && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <BlockStack gap="100">
                    <Text as="h2" variant="headingMd">Current Plan</Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '20px', fontWeight: 700 }}>{currentPlan.name}</span>
                      {currentPlan.price > 0 && (
                        <span style={{
                          background: 'var(--modera-gold)', color: 'var(--modera-black)',
                          fontSize: '12px', fontWeight: 700, padding: '2px 10px', borderRadius: '10px',
                        }}>
                          {`$${currentPlan.price}/mo`}
                        </span>
                      )}
                      {currentPlan.price === 0 && (
                        <Badge>Free</Badge>
                      )}
                    </div>
                  </BlockStack>
                  {currentPlan.price > 0 && (
                    <Button
                      tone="critical"
                      variant="plain"
                      onClick={handleManageSubscription}
                      loading={subscribing === 'manage'}
                    >
                      Manage subscription
                    </Button>
                  )}
                </div>

                {usage && (
                  <>
                    <Divider />
                    <BlockStack gap="200">
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text as="p" variant="bodyMd">Try-on usage this month</Text>
                        <Text as="p" variant="bodyMd" fontWeight="bold">
                          {(usage.used ?? 0).toLocaleString()} / {(usage.limit ?? 0).toLocaleString()}
                        </Text>
                      </div>
                      <ProgressBar
                        progress={usagePercent}
                        tone={usagePercent >= 90 ? 'critical' : 'highlight'}
                        size="small"
                      />
                    </BlockStack>
                  </>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* ── Plan cards ── */}
        <Layout.Section>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">Choose a Plan</Text>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <Spinner size="large" />
              </div>
            ) : (
              <InlineGrid columns={{ xs: 1, sm: 2, md: 3 }} gap="400">
                {plans.map(plan => {
                  const isCurrent = plan.slug === currentPlan?.slug;
                  const isLoading = subscribing === plan.slug;

                  return (
                    <div key={plan.id} style={{ position: 'relative' }}>
                      {plan.is_popular && (
                        <div style={{
                          position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)',
                          background: 'linear-gradient(135deg, var(--modera-gold), var(--modera-gold-dark))',
                          color: 'var(--modera-black)', fontSize: '11px', fontWeight: 700,
                          padding: '3px 12px', borderRadius: '12px', whiteSpace: 'nowrap', zIndex: 1,
                        }}>
                          MOST POPULAR
                        </div>
                      )}
                      <Card>
                        <BlockStack gap="400">
                          <BlockStack gap="100">
                            <Text as="h3" variant="headingMd">{plan.name}</Text>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                              <span style={{ fontSize: '28px', fontWeight: 700, color: 'var(--modera-gold-text-on-light)' }}>
                                {plan.price === 0 ? 'Free' : `$${plan.price}`}
                              </span>
                              {plan.price > 0 && (
                                <Text as="span" variant="bodySm" tone="subdued">/mo</Text>
                              )}
                            </div>
                            <Text as="p" variant="bodySm" tone="subdued">
                              {plan.included_tryons.toLocaleString()} try-ons included
                            </Text>
                          </BlockStack>

                          <Divider />

                          <BlockStack gap="200">
                            {plan.features.map((f, i) => (
                              <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                <span style={{ color: 'var(--modera-gold-text-on-light)', fontWeight: 700, flexShrink: 0 }}>✓</span>
                                <Text as="p" variant="bodySm">{f}</Text>
                              </div>
                            ))}
                          </BlockStack>

                          <Button
                            variant={isCurrent ? 'secondary' : 'primary'}
                            disabled={isCurrent}
                            loading={isLoading}
                            onClick={() => handleSubscribe(plan)}
                            fullWidth
                          >
                            {isCurrent
                              ? 'Current plan'
                              : currentPlan && plan.price < currentPlan.price
                                ? `Downgrade to ${plan.name}`
                                : `Upgrade to ${plan.name}`}
                          </Button>
                        </BlockStack>
                      </Card>
                    </div>
                  );
                })}
              </InlineGrid>
            )}
          </BlockStack>
        </Layout.Section>

      </Layout>
    </Page>
  );
}
